import { useEffect, useRef } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  useGetManagedPackThread,
  useReplyToManagedPackThread,
} from "@/payroll/pack-chat/manager-query";
import { PackChatAiStatus } from "@/payroll/pack-chat/pack-chat-ai-status";
import { PackChatRefreshError } from "@/payroll/pack-chat/pack-chat-refresh-error";
import { PackChatReplyForm } from "@/payroll/pack-chat/pack-chat-reply-form";
import { PackChatConversationSkeleton } from "@/payroll/pack-chat/pack-chat-skeletons";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { PackChatTranscript } from "@/payroll/pack-chat/pack-chat-transcript";
import { isTemporaryPackChatError } from "@/payroll/pack-chat/thread-error-copy";
import { getPackChatThreadErrorCopy } from "@/payroll/pack-chat/thread-error-copy";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";
import {
  PACK_CHAT_CONVERSATION_COLUMN,
  PackChatConversationHeader,
} from "./pack-chat-conversation-header";

const FOLLOW_MESSAGES_THRESHOLD_PX = 80;

/** The fixed header, scrolling transcript, and fixed reply field for one thread. */
export const PackChatConversation = ({
  businessId,
  periodStart,
  runId,
  thread,
  technicianName,
  onReplied,
}: {
  businessId: string | null;
  periodStart: string;
  runId: string | null;
  thread: IPackChatThreadSummary;
  technicianName: string;
  /** Told which question was just answered, so the reader keeps looking at it. */
  onReplied?: (threadId: string) => void;
}) => {
  const detailQuery = useGetManagedPackThread(
    businessId,
    periodStart,
    thread.id,
  );
  const reply = useReplyToManagedPackThread(businessId, periodStart);
  const detail = detailQuery.data?.thread;
  const refreshFailed = Boolean(
    detail && isTemporaryPackChatError(detailQuery.error),
  );
  const current = detail ?? thread;
  const open = current.status === "open";
  const transcript = useRef<HTMLDivElement>(null);
  // The panel keys this component by thread ID, so each conversation starts at the bottom.
  const followMessages = useRef(true);

  useEffect(() => {
    const pane = transcript.current;
    if (pane && followMessages.current) pane.scrollTop = pane.scrollHeight;
  }, [thread.id, detail?.messages.length]);

  if (detailQuery.isError && !refreshFailed)
    return (
      <PackChatState
        fill
        error={detailQuery.error}
        {...getPackChatThreadErrorCopy(detailQuery.error)}
        action={
          <Button size="sm" onClick={() => void detailQuery.refetch()}>
            Try again
          </Button>
        }
      />
    );
  if (detailQuery.isLoading)
    return (
      <Stack gap="none" fullWidth className="h-full min-h-0">
        <span role="status" className="sr-only">
          Loading conversation
        </span>
        <PackChatConversationSkeleton />
      </Stack>
    );

  return (
    <Stack fullWidth gap="none" align="stretch" className="h-full min-h-0">
      <PackChatConversationHeader
        businessId={businessId}
        periodStart={periodStart}
        runId={runId}
        thread={current}
        technicianName={technicianName}
      />

      <Stack
        ref={transcript}
        onScroll={(event) => {
          const pane = event.currentTarget;
          followMessages.current =
            pane.scrollHeight - pane.scrollTop - pane.clientHeight <=
            FOLLOW_MESSAGES_THRESHOLD_PX;
        }}
        gap="none"
        align="stretch"
        className="quiet-scrollbar min-h-0 flex-1 overflow-y-auto px-5 py-4"
      >
        <Stack
          gap="none"
          align="stretch"
          className={PACK_CHAT_CONVERSATION_COLUMN}
        >
          {refreshFailed && (
            <PackChatRefreshError onRetry={() => void detailQuery.refetch()} />
          )}
          {detail ? (
            <>
              <PackChatTranscript
                messages={detail.messages}
                sourceAccess={{
                  businessId,
                  technicianId: thread.technician.id,
                }}
              />
              <PackChatAiStatus thread={detail} managerView />
            </>
          ) : null}
        </Stack>
      </Stack>

      <Stack
        gap="xs"
        align="stretch"
        fullWidth
        className="shrink-0 border-t border-stroke-tertiary px-5 py-3"
      >
        <Stack
          gap="none"
          align="stretch"
          className={PACK_CHAT_CONVERSATION_COLUMN}
        >
          {current.response_owner === "ai" && (
            <Typography variant="bodyW3" textColor="secondary" className="pb-2">
              Reply to take over this conversation. AI replies will stop.
            </Typography>
          )}
          <PackChatReplyForm
            loading={reply.isPending}
            reopens={!open}
            onReply={(body) =>
              reply.mutateAsync({ threadId: thread.id, body }).then(() => {
                onReplied?.(thread.id);
              })
            }
          />
          {!open && current.closed_by && (
            <Typography
              variant="bodyW3"
              textColor="tertiary"
              className="pt-1.5 text-[13px]"
            >
              Closed by {current.closed_by.name}. Replying reopens it.
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
